import { type ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, Home, LayoutGrid, LogOut, Moon, PanelLeft, Shield, Sun, UserCircle, X } from "lucide-react";
import { useTheme } from "@/hooks/use-theme";
import { ADMIN_DOMAIN_NAV, resolveAdminDomain, type AdminTabId } from "./admin-nav";
import { AdminBottomNav } from "./AdminBottomNav";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/client-telemetry";
import { ReleaseInfoFooter } from "@/components/platform/ReleaseInfoFooter";

/**
 * Vista del Admin en desktop: "hub" (cards + franja de dominios, la de siempre) o "sidebar" (menú lateral fijo).
 * Es una preferencia de cada staff guardada en el navegador. En mobile siempre es Hub + barra inferior.
 */
type AdminLayoutMode = "sidebar" | "hub";
const LAYOUT_MODE_KEY = "structura_admin_layout_mode";

/** Arranca en "hub" (evita mismatch de hidratación en SSR) y aplica la preferencia guardada al montar. */
function useAdminLayoutMode() {
  const [mode, setMode] = useState<AdminLayoutMode>("hub");

  useEffect(() => {
    try {
      if (window.localStorage.getItem(LAYOUT_MODE_KEY) === "sidebar") setMode("sidebar");
    } catch {
      /* localStorage no disponible (privacidad/SSR) — se mantiene el default */
    }
  }, []);

  const toggle = () => {
    const next: AdminLayoutMode = mode === "sidebar" ? "hub" : "sidebar";
    setMode(next);
    try {
      window.localStorage.setItem(LAYOUT_MODE_KEY, next);
    } catch {
      /* ignorar */
    }
    trackEvent("admin.layout_mode_change", { mode: next });
  };

  return { mode, toggle };
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari no implementa display-mode: standalone en PWAs agregadas al inicio.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

/**
 * PWA propia del panel Admin — manifest y scope /admin, separados del storefront
 * y de Picking. Solo se monta acá, es decir solo para staff ya autenticado
 * (admin.tsx ya filtró por isPlatformStaffRole antes de llegar a este shell).
 */
const DISMISS_KEY = "structura_admin_pwa_install_dismissed";

interface RelatedApp {
  platform: string;
}

function useAdminPwaInstall() {
  const [installed, setInstalled] = useState(() => isStandalone());
  const [canInstall, setCanInstall] = useState(false);
  const [alreadyInstalledElsewhere, setAlreadyInstalledElsewhere] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (installed) return;
    const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<RelatedApp[]> };
    if (typeof nav.getInstalledRelatedApps !== "function") return;
    // Best-effort: si el browser reporta que la PWA ya está instalada, mostramos un
    // hint para abrirla en vez del CTA de instalación — nunca redirigimos solos
    // (evita loops entre browser y PWA, ver alcance PWA de SCRUM-258).
    nav
      .getInstalledRelatedApps()
      .then((apps) => setAlreadyInstalledElsewhere(apps.length > 0))
      .catch(() => undefined);
  }, [installed]);

  useEffect(() => {
    document.querySelectorAll('link[rel="manifest"]').forEach((el) => el.parentElement?.removeChild(el));
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/api/admin/manifest";
    document.head.appendChild(link);

    if ("serviceWorker" in navigator) {
      if (import.meta.env.DEV) {
        void navigator.serviceWorker.getRegistrations().then((regs) => {
          for (const r of regs) {
            try {
              if (new URL(r.scope).pathname.startsWith("/admin")) void r.unregister();
            } catch {
              /* ignore */
            }
          }
        });
      } else {
        void navigator.serviceWorker.register("/admin-sw.js", { scope: "/admin" }).catch(() => undefined);
      }
    }

    const onPrompt = (e: Event) => {
      e.preventDefault();
      promptRef.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    const prompt = promptRef.current;
    if (!prompt) return;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    promptRef.current = null;
    setCanInstall(false);
  };

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignorar */
    }
    trackEvent("admin.pwa_install_dismissed", {});
  };

  return { installed, canInstall, alreadyInstalledElsewhere, dismissed, install, dismiss };
}

/**
 * CTA contextual de instalación — aparece si el browser ofreció beforeinstallprompt
 * y no se descartó. Si el browser ya reporta la PWA instalada (getInstalledRelatedApps),
 * mostramos un hint para abrirla en vez de ofrecer instalar de nuevo — nunca redirigimos
 * solos (evitar loops browser↔PWA).
 */
function AdminInstallBanner({
  show,
  alreadyInstalledElsewhere,
  onInstall,
  onDismiss,
}: {
  show: boolean;
  alreadyInstalledElsewhere: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}) {
  if (alreadyInstalledElsewhere) {
    return (
      <div className="flex items-center gap-3 border-b border-border bg-secondary/40 px-4 py-2">
        <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          Ya tenés Structura Admin instalada — abrila desde tu pantalla de inicio para la mejor experiencia.
        </p>
      </div>
    );
  }
  if (!show) return null;
  return (
    <div className="flex items-center gap-3 border-b border-border bg-primary/10 px-4 py-2">
      <Download className="h-4 w-4 shrink-0 text-primary" />
      <p className="min-w-0 flex-1 truncate text-xs text-foreground">
        Instalá Structura Admin como app para acceso rápido y pantalla completa.
      </p>
      <Button size="sm" className="h-7 shrink-0 text-xs" onClick={onInstall}>
        Instalar
      </Button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Descartar sugerencia de instalación"
        className="shrink-0 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

type Props = {
  tab: AdminTabId;
  onTabChange: (id: AdminTabId) => void;
  leadsPending?: number;
  userEmail?: string;
  onLogout: () => void;
  /** Matriz de planes necesita más ancho que el resto del panel. */
  wideContent?: boolean;
  children: ReactNode;
};

/** Menú lateral fijo de dominios — solo desktop y solo con la vista "sidebar". */
function SidebarNav({
  tab,
  onTabChange,
  leadsPending,
}: {
  tab: AdminTabId;
  onTabChange: (id: AdminTabId) => void;
  leadsPending?: number;
}) {
  const location = useLocation();
  const activeDomain = resolveAdminDomain({ tab, pathname: location.pathname });

  return (
    <aside
      className="fixed left-0 top-14 z-30 hidden h-[calc(100vh-3.5rem)] w-[240px] flex-col border-r border-border bg-sidebar md:flex"
      aria-label="Dominios de Admin"
    >
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
        {ADMIN_DOMAIN_NAV.map(({ id, label, icon: Icon, targetTab }) => (
          <button
            key={id}
            type="button"
            onClick={() => {
              trackEvent("admin.tab_change", { tab: targetTab, domain: id });
              onTabChange(targetTab);
            }}
            className={cn(
              "flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm",
              activeDomain === id && tab !== "hub"
                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                : "text-muted-foreground hover:bg-sidebar-accent/50",
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{label}</span>
            {id === "negocio" && leadsPending != null && leadsPending > 0 && (
              <Badge variant="secondary" className="h-5 min-w-5 justify-center px-1.5 text-[10px]">
                {leadsPending}
              </Badge>
            )}
          </button>
        ))}
      </nav>
    </aside>
  );
}

/** Franja horizontal de dominios — la navegación de desktop en la vista "hub". */
function DomainStrip({
  tab,
  onTabChange,
  leadsPending,
}: {
  tab: AdminTabId;
  onTabChange: (id: AdminTabId) => void;
  leadsPending?: number;
}) {
  const location = useLocation();
  const activeDomain = resolveAdminDomain({ tab, pathname: location.pathname });

  return (
    <nav
      className="hidden items-center gap-1 overflow-x-auto border-b border-border px-4 py-2 md:flex"
      aria-label="Dominios de Admin"
    >
      {ADMIN_DOMAIN_NAV.map(({ id, label, icon: Icon, targetTab }) => (
        <button
          key={id}
          type="button"
          onClick={() => {
            trackEvent("admin.tab_change", { tab: targetTab, domain: id });
            onTabChange(targetTab);
          }}
          className={cn(
            "flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm whitespace-nowrap",
            activeDomain === id && tab !== "hub"
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span>{label}</span>
          {id === "negocio" && leadsPending != null && leadsPending > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 justify-center px-1.5 text-[10px]">
              {leadsPending}
            </Badge>
          )}
        </button>
      ))}
    </nav>
  );
}

export function PlatformAdminShell({
  tab,
  onTabChange,
  leadsPending,
  userEmail,
  onLogout,
  wideContent,
  children,
}: Props) {
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const {
    canInstall: canInstallPwa,
    alreadyInstalledElsewhere: pwaInstalledElsewhere,
    dismissed: pwaDismissed,
    install: installPwa,
    dismiss: dismissPwa,
  } = useAdminPwaInstall();
  const { mode: layoutMode, toggle: toggleLayoutMode } = useAdminLayoutMode();
  const withSidebar = layoutMode === "sidebar";
  const isOps = location.pathname.startsWith("/admin/ops");
  /** wideContent solo amplía max-width; el scroll de página queda libre (sticky solo en thead de matriz). */
  const contentMax = isOps ? "" : wideContent ? "max-w-[88rem]" : "max-w-5xl";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="flex h-14 items-center gap-2 px-4">
          <button
            type="button"
            onClick={() => onTabChange("hub")}
            className={cn(
              "flex items-center gap-2 rounded-lg px-2 py-1.5 text-left",
              tab === "hub" ? "bg-primary/10 text-primary" : "hover:bg-accent",
            )}
            aria-label="Ir al Hub de Admin"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
              <Shield className="h-4 w-4" />
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-bold leading-none">Super Admin</p>
              <Badge variant="outline" className="mt-1 text-[10px] border-amber-500/40 text-amber-400">
                Structura Market
              </Badge>
            </div>
          </button>

          <Button
            variant="ghost"
            size="sm"
            className="ml-1 gap-1.5"
            onClick={() => onTabChange("hub")}
          >
            <Home className="h-4 w-4" /> <span className="hidden sm:inline">Hub</span>
          </Button>

          <span className="flex-1" />

          <Button
            variant="ghost"
            size="icon"
            title={withSidebar ? "Cambiar a vista Hub" : "Cambiar a vista con menú lateral"}
            aria-label={withSidebar ? "Cambiar a vista Hub" : "Cambiar a vista con menú lateral"}
            onClick={toggleLayoutMode}
            className="hidden h-8 w-8 shrink-0 md:inline-flex"
          >
            {withSidebar ? <LayoutGrid className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
          </Button>

          <Button
            variant="ghost" size="icon"
            title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            onClick={toggleTheme}
            className="h-8 w-8 shrink-0"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Cuenta">
                <UserCircle className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
                {userEmail}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">
                <ReleaseInfoFooter productLabel="Structura Market" compact />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="gap-2 text-destructive focus:text-destructive">
                <LogOut className="h-3.5 w-3.5" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {!withSidebar && <DomainStrip tab={tab} onTabChange={onTabChange} leadsPending={leadsPending} />}
      </header>

      {withSidebar && <SidebarNav tab={tab} onTabChange={onTabChange} leadsPending={leadsPending} />}

      <div className={cn(withSidebar && "md:pl-[240px]")}>
        <AdminInstallBanner
          show={canInstallPwa && !pwaDismissed}
          alreadyInstalledElsewhere={pwaInstalledElsewhere}
          onInstall={() => void installPwa()}
          onDismiss={dismissPwa}
        />

        <div
          className={cn(
            isOps ? "" : "px-4 py-8 pb-24 sm:px-8 md:pb-8",
          )}
        >
          <div
            className={cn(
              isOps ? "pb-24 md:pb-0" : "mx-auto",
              contentMax,
            )}
          >
            {children}
          </div>
        </div>
      </div>

      <AdminBottomNav
        tab={tab}
        pathname={location.pathname}
        onTabChange={onTabChange}
        leadsPending={leadsPending}
      />
    </div>
  );
}
